/**
 * useCreateSubscription - Hook for managing subscription creation flow
 *
 * Used when a canceled user needs to create a new subscription.
 * Handles the complete Stripe PaymentIntent flow:
 * 1. Create subscription via API (returns PaymentIntent)
 * 2. Confirm with Stripe Elements
 * 3. Handle success/error states
 *
 * Also supports Express Checkout (Apple Pay/Google Pay).
 */

import {
  useCreateResubscriptionV1SubscriptionsResubscribePost,
  useConfirmResubscriptionV1SubscriptionsResubscribeConfirmPost,
} from '@/api/generated-catch-backend/subscriptions/subscriptions';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import { useCallback, useState } from 'react';
import { useMe } from '@/context/me/me.context';
import type { StripeExpressCheckoutElementConfirmEvent } from '@stripe/stripe-js';

export type SubscriptionCreateState =
  | 'idle'
  | 'creating_subscription'
  | 'confirming'
  | 'success'
  | 'error';

interface UseCreateSubscriptionOptions {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

interface UseCreateSubscriptionResult {
  state: SubscriptionCreateState;
  error: string | null;
  createSubscription: (zipCode?: string, coupon?: string) => Promise<void>;
  handleExpressCheckout: (event: StripeExpressCheckoutElementConfirmEvent) => Promise<void>;
  reset: () => void;
}

export function useCreateSubscription({
  onSuccess,
  onError,
}: UseCreateSubscriptionOptions): UseCreateSubscriptionResult {
  const [state, setState] = useState<SubscriptionCreateState>('idle');
  const [error, setError] = useState<string | null>(null);

  const stripe = useStripe();
  const elements = useElements();
  const { data: userData, refetch: refetchMe } = useMe();

  const createSubscriptionMutation = useCreateResubscriptionV1SubscriptionsResubscribePost();
  const confirmSubscriptionMutation = useConfirmResubscriptionV1SubscriptionsResubscribeConfirmPost();

  /**
   * Create subscription with card payment
   */
  const createSubscription = useCallback(
    async (zipCode?: string, coupon?: string) => {
      if (!stripe || !elements) {
        setError('Stripe not initialized');
        setState('error');
        return;
      }

      // Email is stored in account.credential
      const email = userData?.account?.credential;
      if (!email) {
        setError('User email not available');
        setState('error');
        onError?.('User email not available');
        return;
      }

      try {
        // Step 1: Validate the form
        const { error: submitError } = await elements.submit();
        if (submitError) {
          setError(submitError.message ?? 'Please check your payment information');
          setState('error');
          onError?.(submitError.message ?? 'Please check your payment information');
          return;
        }

        // Step 2: Create subscription via catch-backend
        setState('creating_subscription');
        const { type, clientSecret, subscriptionId } = await createSubscriptionMutation.mutateAsync({
          data: {
            email,
            coupon: coupon || undefined,
            zipCode: zipCode || undefined,
          },
        });

        // Step 3: Confirm payment/setup intent with Stripe
        setState('confirming');
        const confirmMethod = type === 'payment' ? stripe.confirmPayment : stripe.confirmSetup;

        const { error: confirmError } = await confirmMethod({
          elements,
          clientSecret,
          confirmParams: {
            payment_method_data: {
              billing_details: {
                address: {
                  country: 'US',
                  postal_code: zipCode,
                },
              },
            },
          },
          redirect: 'if_required',
        });

        if (confirmError) {
          setError(confirmError.message ?? 'Payment failed');
          setState('error');
          onError?.(confirmError.message ?? 'Payment failed');
          return;
        }

        // Step 4: Confirm subscription to sync with backend
        await confirmSubscriptionMutation.mutateAsync({
          data: { subscriptionId },
        });

        // Refetch /me to get updated userLevel (now 'member')
        // This prevents paywall from reappearing due to cached 'canceled' state
        await refetchMe();

        // Success!
        setState('success');
        setError(null);
        onSuccess?.();
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to create subscription. Please try again.';
        setError(errorMessage);
        setState('error');
        onError?.(errorMessage);
      }
    },
    [stripe, elements, userData?.account?.credential, createSubscriptionMutation, confirmSubscriptionMutation, refetchMe, onSuccess, onError]
  );

  /**
   * Handle Express Checkout (Apple Pay/Google Pay) confirmation
   */
  const handleExpressCheckout = useCallback(
    async (event: StripeExpressCheckoutElementConfirmEvent) => {
      if (!stripe || !elements) {
        setError('Stripe not initialized');
        setState('error');
        return;
      }

      // Email is stored in account.credential
      const email = userData?.account?.credential;
      if (!email) {
        setError('User email not available');
        setState('error');
        onError?.('User email not available');
        return;
      }

      try {
        setState('creating_subscription');

        const zipCode = event.billingDetails?.address?.postal_code || '';

        // Submit elements first
        const { error: submitError } = await elements.submit();
        if (submitError) {
          setError(submitError.message ?? 'Payment failed');
          setState('error');
          onError?.(submitError.message ?? 'Payment failed');
          return;
        }

        // Create subscription via catch-backend
        const { type, clientSecret, subscriptionId } = await createSubscriptionMutation.mutateAsync({
          data: {
            email,
            zipCode: zipCode || undefined,
          },
        });

        // Confirm with Stripe
        setState('confirming');
        const confirmMethod = type === 'payment' ? stripe.confirmPayment : stripe.confirmSetup;

        const { error: confirmError } = await confirmMethod({
          elements,
          clientSecret,
          confirmParams: {
            payment_method_data: {
              billing_details: {
                address: {
                  country: 'US',
                  postal_code: zipCode,
                },
              },
            },
          },
          redirect: 'if_required',
        });

        if (confirmError) {
          setError(confirmError.message ?? 'Payment failed');
          setState('error');
          onError?.(confirmError.message ?? 'Payment failed');
          return;
        }

        // Confirm subscription to sync with backend
        await confirmSubscriptionMutation.mutateAsync({
          data: { subscriptionId },
        });

        // Refetch /me to get updated userLevel (now 'member')
        await refetchMe();

        setState('success');
        setError(null);
        onSuccess?.();
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Payment failed. Please try again.';
        setError(errorMessage);
        setState('error');
        onError?.(errorMessage);
      }
    },
    [stripe, elements, userData?.account?.credential, createSubscriptionMutation, confirmSubscriptionMutation, refetchMe, onSuccess, onError]
  );

  const reset = useCallback(() => {
    setState('idle');
    setError(null);
  }, []);

  return {
    state,
    error,
    createSubscription,
    handleExpressCheckout,
    reset,
  };
}
